import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MOVES } from "@/lib/preventionData";

const BLUE = "#4C9EDA";
const DEEP = "#2E6FA3";
const INK = "#122534";

export default function Moves() {
  const [by, setBy] = useState("effect");
  const [on, setOn] = useState(0);
  const list = useMemo(() => (by === "effect" ? MOVES : [...MOVES].sort((a, b) => a.effort - b.effort)), [by]);

  return (
    <section className="relative" style={{ background: "linear-gradient(180deg, #F2F7FB, #E8F1F8)" }} data-testid="prevention-moves">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-20">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-9">
          <div className="max-w-xl">
            <p className="text-[10px] tracking-[0.24em] uppercase" style={{ color: "rgba(18,37,52,0.4)" }}>The moves that matter</p>
            <h2 className="font-display text-[32px] sm:text-[44px] leading-[1.02] mt-2" style={{ color: INK }}>Ranked by effect, not by fashion</h2>
          </div>
          <div className="flex gap-1.5 p-1 rounded-full" style={{ background: "rgba(18,37,52,0.06)" }}>
            {[["effect", "By effect"], ["effort", "By effort"]].map(([k, l]) => (
              <button key={k} onClick={() => setBy(k)} className="rounded-full px-4 py-2 text-[12.5px] font-semibold transition-colors"
                style={by === k ? { background: "#fff", color: INK, boxShadow: "0 8px 18px -10px rgba(18,37,52,0.5)" } : { color: "rgba(18,37,52,0.45)" }} data-testid={`moves-by-${k}`}>{l}</button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7 space-y-2.5">
            {list.map((m, i) => {
              const active = on === m.effect;
              return (
                <motion.button key={m.name} layout onClick={() => setOn(m.effect)} onMouseEnter={() => setOn(m.effect)}
                  className="w-full text-left" data-testid={`move-${i}`}>
                  <div className="flex items-baseline justify-between gap-3">
                    <p className="text-[14.5px]" style={{ color: active ? INK : "rgba(18,37,52,0.62)", fontWeight: active ? 600 : 400 }}>{m.name}</p>
                    <p className="text-[11.5px] shrink-0 tabular-nums" style={{ color: "rgba(18,37,52,0.4)" }}>{by === "effect" ? m.years : `effort ${m.effort}`}</p>
                  </div>
                  <div className="h-3 rounded-full mt-1.5 overflow-hidden" style={{ background: "rgba(18,37,52,0.07)" }}>
                    <motion.span className="block h-full rounded-full" initial={{ width: 0 }} whileInView={{ width: `${by === "effect" ? m.effect : 100 - m.effort}%` }} viewport={{ once: true }}
                      transition={{ duration: 0.9, delay: Math.min(i * 0.05, 0.4), ease: [0.22, 1, 0.36, 1] }}
                      style={{ background: active ? `linear-gradient(90deg, ${BLUE}, ${DEEP})` : "rgba(18,37,52,0.2)" }} />
                  </div>
                </motion.button>
              );
            })}
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-[1.8rem] p-7 sticky top-24" style={{ background: "#fff", border: "1px solid rgba(18,37,52,0.08)", boxShadow: "0 40px 80px -60px rgba(18,37,52,0.9)" }}>
              <AnimatePresence mode="wait">
                {MOVES.filter((m) => m.effect === on).map((m) => (
                  <motion.div key={m.name} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.3 }}>
                    <p className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: DEEP }}>adds roughly {m.years}</p>
                    <p className="font-display text-[26px] mt-2 leading-tight" style={{ color: INK }}>{m.name}</p>
                    <p className="text-[14.5px] mt-3.5 leading-relaxed" style={{ color: "rgba(18,37,52,0.62)" }}>{m.note}</p>
                    <div className="flex gap-6 mt-6">
                      <div>
                        <p className="font-display text-2xl tabular-nums" style={{ color: BLUE }}>{m.effect}</p>
                        <p className="text-[11px]" style={{ color: "rgba(18,37,52,0.4)" }}>relative effect</p>
                      </div>
                      <div>
                        <p className="font-display text-2xl tabular-nums" style={{ color: "rgba(18,37,52,0.4)" }}>{m.effort}</p>
                        <p className="text-[11px]" style={{ color: "rgba(18,37,52,0.4)" }}>how hard it is</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
